import { useEffect, useMemo, useState } from 'react';
import type { CwServiceCategoryAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwServiceCategoryAliases, useSetCwServiceCategoryAliases } from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwCategoryName: string;
  serviceCategoryId: string;
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `svc-alias-${aliasSeq}`;
}

interface ServiceCategoryAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise product category (a dropdown) onto a PSA service category (a
 * dropdown). Each row links a CW category name to an existing PSA service category.
 * On the products sync, a catalog item's category is resolved through these rows and
 * sets the product's service category; CW categories without a row here leave the
 * product's service category unchanged.
 */
export function ServiceCategoryAliasEditor({
  isOpen,
}: ServiceCategoryAliasEditorProps): JSX.Element {
  const configQuery = useCwServiceCategoryAliases(isOpen);
  const saveAliases = useSetCwServiceCategoryAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwCategoryName: alias.cwCategoryName,
        serviceCategoryId: alias.serviceCategoryId,
      })),
    );
  }, [config]);

  const serviceCategories = config?.serviceCategories ?? [];
  const discoveredCategories = config?.discoveredCategories ?? [];
  const live = config?.live ?? false;

  // CW category names already mapped, so we can hint which discovered ones remain.
  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwCategoryName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredCategories.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (cwCategoryName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwCategoryName, serviceCategoryId: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwServiceCategoryAliasDto[] = rows
      .filter((row) => row.cwCategoryName.trim() && row.serviceCategoryId)
      .map((row) => ({
        cwCategoryName: row.cwCategoryName.trim(),
        serviceCategoryId: row.serviceCategoryId,
      }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-product-categories';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Service category (dropdown)</h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise stores a product&apos;s category as a dropdown. Map each ConnectWise category
          to a PSA service category below. On sync, the matched service category is set on the
          product — ConnectWise categories without a row here leave the product&apos;s service
          category unchanged.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading service categories…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load service categories.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise product categories loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise categories discovered yet. Configure the connection to load suggestions
              — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredCategories.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the service-category aliases. Make sure each row targets a valid
              service category.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No service-category aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise category</th>
                  <th className="py-2 pr-3 font-medium">PSA service category</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwCategoryName}
                        placeholder="e.g. Hardware"
                        onChange={(e) => updateRow(row.id, { cwCategoryName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.serviceCategoryId}
                        onChange={(e) => updateRow(row.id, { serviceCategoryId: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select category —</option>
                        {serviceCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save service categories
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
